import React, {useEffect, useRef, useState} from 'react';
import * as css from './CodeInput.css';
import {useSelector} from 'react-redux';
import setGlobalState from '../../redux/setGlobalState';

const CODE_LENGTH = 6;
const EMPTY_ARRAY = Array(CODE_LENGTH).fill('');

function CodeInput({onUpdate = () => {}}) {
    const isWrong = useSelector(state => state.ui.codeInput.isWrong);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [values, setValues] = useState(EMPTY_ARRAY);
    const inputRefs = useRef(Array(CODE_LENGTH).fill(null).map(() => useRef(null)));
    
    // Track whether a state update is in progress to avoid loops
    const stateUpdateInProgress = useRef(false);
    const otpControllerRef = useRef(null);
    const isWebOTPInProgress = useRef(false);

    const reset = () => {
        setCurrentIndex(0);
        setValues(EMPTY_ARRAY);
    };

    const getWebOtp = () => {
        // Check if WebOTP is supported
        if('OTPCredential' in window && navigator.credentials) {
            // Clean up any existing controller
            if(otpControllerRef.current) {
                otpControllerRef.current.abort();
            }
            
            const ac = new AbortController();
            otpControllerRef.current = ac;
            
            // Detect Chrome on iOS
            const isChromeOnIOS = /CriOS/.test(navigator.userAgent);
            
            // Chrome on iOS needs different handling
            if(isChromeOnIOS) {
                // Don't use AbortController signal for Chrome on iOS
                navigator.credentials.get({
                    otp: {transport: ['sms']}
                }).then(otp => {
                    if(otp && otp.code && !ac.signal.aborted) {
                        isWebOTPInProgress.current = true;
                        const newValues = otp.code.split('');
                        if(newValues.length === CODE_LENGTH && newValues.every(value => /^[0-9]$/.test(value))) {
                            // Set all values at once
                            setValues(newValues);
                            setCurrentIndex(CODE_LENGTH - 1); // Move to last field
                            onUpdate(newValues.join(''));
                            setGlobalState('ui.codeInput.code', newValues.join(''));
                            
                            // Reset flag after a brief delay
                            setTimeout(() => {
                                isWebOTPInProgress.current = false;
                            }, 100);
                        } else {
                            isWebOTPInProgress.current = false;
                        }
                    }
                }).catch(error => {
                    isWebOTPInProgress.current = false;
                    if(error.name !== 'AbortError') {
                        console.log('WebOTP failed on Chrome iOS:', error);
                    }
                }).finally(() => {
                    otpControllerRef.current = null;
                });
            } else {
                // Standard implementation for other browsers
                const timeoutId = setTimeout(() => {
                    if(!ac.signal.aborted) {
                        console.log('WebOTP request timed out');
                        ac.abort();
                    }
                }, 60000);
                
                navigator.credentials.get({
                    otp: {transport: ['sms']},
                    signal: ac.signal
                }).then(otp => {
                    if(otp && otp.code) {
                        isWebOTPInProgress.current = true;
                        const newValues = otp.code.split('');
                        if(newValues.length === CODE_LENGTH && newValues.every(value => /^[0-9]$/.test(value))) {
                            setValues(newValues);
                            setCurrentIndex(CODE_LENGTH - 1); // Move to last field
                            onUpdate(newValues.join(''));
                            setGlobalState('ui.codeInput.code', newValues.join(''));
                            
                            // Reset flag after a brief delay
                            setTimeout(() => {
                                isWebOTPInProgress.current = false;
                            }, 100);
                        } else {
                            isWebOTPInProgress.current = false;
                        }
                    }
                }).catch(error => {
                    isWebOTPInProgress.current = false;
                    if(error.name !== 'AbortError') {
                        console.log('WebOTP failed:', error);
                    }
                }).finally(() => {
                    clearTimeout(timeoutId);
                    otpControllerRef.current = null;
                });
            }
        }
    };

    // Focus management
    useEffect(() => {
        if(inputRefs.current[currentIndex]?.current) {
            inputRefs.current[currentIndex].current.focus();
        }
    }, [currentIndex]);

    // Wrong input handling
    useEffect(() => {
        if(isWrong && !stateUpdateInProgress.current) {
            stateUpdateInProgress.current = true;
            setTimeout(() => {
                reset();
                stateUpdateInProgress.current = false;
            }, 0);
        }
    }, [isWrong]);

    // Initialize WebOTP with cleanup
    useEffect(() => {
        // For Chrome on iOS, we need to ensure the first field has focus for WebOTP
        const isChromeOnIOS = /CriOS/.test(navigator.userAgent);
        if(isChromeOnIOS && inputRefs.current[0]?.current) {
            inputRefs.current[0].current.focus();
        }
        
        // Small delay to ensure the component is fully mounted
        const timer = setTimeout(() => {
            getWebOtp();
        }, 100);

        return () => {
            clearTimeout(timer);
            // Clean up WebOTP request on unmount
            if(otpControllerRef.current) {
                otpControllerRef.current.abort();
            }
            setGlobalState('ui.codeInput', {expiredTime: null, code: '', isWrong: false});
        };
    }, []);

    const onPaste = (e) => {
        // Ignore paste events when WebOTP is in progress
        if(isWebOTPInProgress.current) {
            return;
        }
        
        const val = e.clipboardData.getData('Text').replace(/\D/g, '').split('');
        if(val.length !== CODE_LENGTH) {
            return;
        }
        
        e.preventDefault();
        isWebOTPInProgress.current = true;
        
        setValues(val);
        setCurrentIndex(CODE_LENGTH - 1); // Move to last field
        onUpdate(val.join(''));
        setGlobalState('ui.codeInput.code', val.join(''));
        
        if(isWrong) {
            setGlobalState('ui.codeInput.isWrong', false);
        }
        
        // Reset flag after a brief delay
        setTimeout(() => {
            isWebOTPInProgress.current = false;
        }, 100);
    };

    const onKeyDown = (e) => {
        // Ignore key events when WebOTP is in progress
        if(isWebOTPInProgress.current) {
            return;
        }
        
        const index = parseInt(e.target.name, 10);
        
        if(e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            let newValues = [...values];
            let shouldUpdateIndex = false;
            let newIndex = currentIndex;
            
            newValues[index] = '';
            
            if(e.key === 'Delete' && index < CODE_LENGTH - 1) {
                newIndex = index + 1;
                shouldUpdateIndex = true;
            } else if(e.key === 'Backspace' && index > 0) {
                newIndex = index - 1;
                shouldUpdateIndex = true;
            }
            
            setValues(newValues);
            
            if(shouldUpdateIndex) {
                setCurrentIndex(newIndex);
            }
            
            // Update global state
            setGlobalState('ui.codeInput.code', newValues.join(''));
            
            if(isWrong) {
                setGlobalState('ui.codeInput.isWrong', false);
            }
        }
    };

    const onInput = (e) => {
        const value = e.target.value;
        const index = parseInt(e.target.name, 10);
        
        // Detect Chrome on iOS
        const isChromeOnIOS = /CriOS/.test(navigator.userAgent);
        
        // Special handling for Chrome on iOS - it often fills the first field with full code
        if(isChromeOnIOS && index === 0 && value.length >= CODE_LENGTH) {
            const fullCode = value.substring(0, CODE_LENGTH);
            if(/^\d{6}$/.test(fullCode)) {
                e.preventDefault();
                isWebOTPInProgress.current = true;
                
                const newValues = fullCode.split('');
                setValues(newValues);
                setCurrentIndex(CODE_LENGTH - 1);
                onUpdate(newValues.join(''));
                setGlobalState('ui.codeInput.code', newValues.join(''));
                
                if(isWrong) {
                    setGlobalState('ui.codeInput.isWrong', false);
                }
                
                // Clear the first input to prevent showing extra digits
                e.target.value = newValues[0];
                
                setTimeout(() => {
                    isWebOTPInProgress.current = false;
                }, 200);
                return;
            }
        }
        
        // Ignore input events when WebOTP is in progress (for other cases)
        if(isWebOTPInProgress.current) {
            return;
        }
    
        let newValues = [...values];
        let shouldUpdateIndex = false;
        let newIndex = currentIndex;

        // Handle single digit input
        if(value && value.length === 1 && /^[0-9]$/.test(value)) {
            newValues[index] = value;
      
            if(index < CODE_LENGTH - 1) {
                shouldUpdateIndex = true;
                newIndex = index + 1;
            }
        } 
        // Handle full code paste (for other browsers)
        else if(value.length === CODE_LENGTH && /^\d{6}$/.test(value)) {
            isWebOTPInProgress.current = true;
            newValues = value.split('');
            setValues(newValues);
            setCurrentIndex(CODE_LENGTH - 1);
            onUpdate(newValues.join(''));
            setGlobalState('ui.codeInput.code', newValues.join(''));
            
            if(isWrong) {
                setGlobalState('ui.codeInput.isWrong', false);
            }
            
            setTimeout(() => {
                isWebOTPInProgress.current = false;
            }, 200);
            return;
        }

        setValues(newValues);
        
        if(shouldUpdateIndex) {
            setCurrentIndex(newIndex);
        }

        // Check if the code is complete
        if(newValues.every(val => val !== '')) {
            onUpdate(newValues.join(''));
        }

        setGlobalState('ui.codeInput.code', newValues.join(''));
        
        if(isWrong) {
            setGlobalState('ui.codeInput.isWrong', false);
        }
    };

    const getInput = (index) => {
        const isChromeOnIOS = /CriOS/.test(navigator.userAgent);
        
        return (
            <input 
                name={index.toString()}
                type="tel"
                ref={inputRefs.current[index]}
                value={values[index]}
                onChange={(e) => {
                    // Chrome on iOS might use onChange instead of onInput for WebOTP
                    if(isChromeOnIOS && index === 0 && e.target.value.length >= CODE_LENGTH) {
                        const fullCode = e.target.value.substring(0, CODE_LENGTH);
                        if(/^\d{6}$/.test(fullCode)) {
                            e.preventDefault();
                            isWebOTPInProgress.current = true;
                            
                            const newValues = fullCode.split('');
                            setValues(newValues);
                            setCurrentIndex(CODE_LENGTH - 1);
                            onUpdate(newValues.join(''));
                            setGlobalState('ui.codeInput.code', newValues.join(''));
                            
                            if(isWrong) {
                                setGlobalState('ui.codeInput.isWrong', false);
                            }
                            
                            setTimeout(() => {
                                isWebOTPInProgress.current = false;
                            }, 200);
                            return;
                        }
                    }
                }}
                autoComplete="one-time-code"
                onPaste={onPaste}
                onInput={onInput}
                onKeyDown={onKeyDown}
                className={
                    (isWrong && !values.some(i => i)) 
                        ? 'wrong' 
                        : (values[index] !== '' ? 'full' : '')
                }
                aria-label={`Digit ${index + 1} of verification code`}
                maxLength={isChromeOnIOS && index === 0 ? CODE_LENGTH : 1}
                inputMode="numeric"
            />
        );
    };

    return (
        <css.container>
            {EMPTY_ARRAY.map((_, i) => 
                <React.Fragment key={i}>
                    {i === 3 && <css.colon>-</css.colon>}
                    {getInput(i)}
                </React.Fragment>
            )}
        </css.container>
    );
}

export default CodeInput;
